/**
 * As colunas calculadas da folha de aferição — para CONFERIR, não para
 * gravar.
 *
 * Quem calcula o valor que vale é o banco, na aprovação
 * (`fn_previa_do_certificado`, 0105/0106/0233): esta tela só mostra, ao
 * lado do que se digita, o número que a assinatura vai publicar. Por isso
 * as contas aqui são as MESMAS de lá, e o teste 247 confere as duas uma
 * contra a outra — uma cópia que só concorda consigo mesma não protege
 * nada.
 *
 * ── Duas geometrias, e só duas ────────────────────────────────────────
 *
 *  · CILINDRO — o anel de adensamento e os moldes (ISC, Proctor, Hilf):
 *      V = π · d² / 4 · h                       (cm³, com d e h em mm)
 *  · PRISMA — o anel de cisalhamento direto, que é QUADRADO (onda 260 ·
 *    RN-224; o certificado 2611 mede Largura 1 e Largura 2):
 *      A = L₁ · L₂                              (mm²)
 *      V = L₁ · L₂ · h                          (cm³)
 *
 * Qual conta vale sai do catálogo (`categoria_grandeza.derivada_de`), e
 * não do nome da categoria: o volume que deriva de `diametro` é o do
 * cilindro; o que deriva de `largura_1` é o do prisma. Coluna derivada
 * que esta tela não sabe calcular aparece com «—», e não com um número
 * inventado.
 */

/** Uma coluna da folha, no que importa para a conta. */
export interface ColunaDerivada {
  grandeza: string;
  derivada_de: string[] | null;
}

/** O valor digitado de uma grandeza, já como número (mm, g). */
export type Medida = (grandeza: string) => number | undefined;

const positivo = (v: number | undefined): number | null =>
  (v !== undefined && Number.isFinite(v) && v > 0 ? v : null);

/** V (cm³) = π · d² / 4 · h, com d e h em mm. */
export function volumeDoCilindro(dMm: number, hMm: number): number {
  return Math.PI * (dMm / 10) ** 2 / 4 * (hMm / 10);
}

/** A (mm²) = L₁ · L₂, com L₁ e L₂ em mm. */
export function areaDoRetangulo(l1Mm: number, l2Mm: number): number {
  return l1Mm * l2Mm;
}

/** V (cm³) = L₁ · L₂ · h, com os três em mm. */
export function volumeDoPrisma(l1Mm: number, l2Mm: number, hMm: number): number {
  return (l1Mm * l2Mm * hMm) / 1000;
}

/**
 * O valor de uma coluna derivada, a partir do que foi digitado na linha.
 * `null` quando falta medida, ou quando a conta não é uma das duas.
 */
export function contaDaDerivada(g: ColunaDerivada, medida: Medida): number | null {
  const de = g.derivada_de ?? [];
  const prisma = de.includes('largura_1') && de.includes('largura_2');

  if (g.grandeza === 'area' && prisma) {
    const l1 = positivo(medida('largura_1'));
    const l2 = positivo(medida('largura_2'));
    return l1 === null || l2 === null ? null : areaDoRetangulo(l1, l2);
  }

  if (g.grandeza === 'volume' && prisma) {
    const l1 = positivo(medida('largura_1'));
    const l2 = positivo(medida('largura_2'));
    const h = positivo(medida('altura'));
    return l1 === null || l2 === null || h === null ? null : volumeDoPrisma(l1, l2, h);
  }

  if (g.grandeza === 'volume' && de.includes('diametro')) {
    const d = positivo(medida('diametro'));
    const h = positivo(medida('altura'));
    return d === null || h === null ? null : volumeDoCilindro(d, h);
  }

  return null;
}
